import { Events } from 'src/constants/webSocketConsts/events';
import {
  AIAssistantStatus,
  clearStatus,
  setStatus,
} from 'src/store/aiAssistantStatus/slice';
import { AppDispatch } from 'src/store/configureStore';

interface AIStatusUpdate {
  fields?: {
    channelId?: string;
    status?: string;
    messageId?: string;
    metadata?: {
      tool?: string;
      query?: string;
      errorMessage?: string;
      errorType?: string;
    };
  };
}

const isValidAIAssistantStatus = (
  status: string,
): status is AIAssistantStatus => {
  const validStatuses: Set<string> = new Set([
    'sending',
    'thinking',
    'searching_web',
    'generating_response',
    'using_tools',
    'get_client_context',
    'get_company_context',
    'get_workspace_context',
    'taking_longer',
    'error',
    'canceling',
    'canceled',
    'blocked',
  ]);
  return validStatuses.has(status);
};

export const aiAssistantStatusHandler = (
  _event: Events,
  items: AIStatusUpdate[],
  dispatch: AppDispatch,
): void => {
  items.forEach((item) => {
    // Extract data from fields object (consistent with other WebSocket handlers)
    const channelId = item.fields?.channelId;
    const status = item.fields?.status;
    const messageId = item.fields?.messageId;
    const metadata = item.fields?.metadata;

    if (!channelId) {
      return;
    }

    if (status !== undefined) {
      // Empty string means explicitly clear the status
      if (status === '') {
        dispatch(clearStatus({ channelId }));
        return;
      }

      if (isValidAIAssistantStatus(status)) {
        // Handle 'blocked' status separately - don't update the status indicator
        if (status === 'blocked') {
          return;
        }

        // Always update the status for the channel, regardless of message ID matching
        // This allows backend statuses (with message IDs) to update frontend optimistic statuses (without message IDs)
        dispatch(
          setStatus({
            channelId,
            status,
            metadata,
            messageId,
          }),
        );

        // If status is "canceled", clear it after 2 seconds
        if (status === 'canceled') {
          setTimeout(() => {
            dispatch(clearStatus({ channelId }));
          }, 2000);
        }
      }
    }
    // If status is undefined (not present in the message), don't change anything
    // This prevents accidental clears from unrelated WebSocket messages
  });
};
